import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, Check } from 'lucide-react';
import { gbp } from './quoteCalculator';

export const Step3ReviewPay = ({ 
  form, 
  setForm, 
  quote, 
  finalAmount,
  discountApplied,
  discountPercent,
  discountError,
  termsAccepted,
  setTermsAccepted,
  onApplyDiscount,
  onRemoveDiscount,
  setDiscountError,
  onBack, 
  onSubmit,
  busy,
  error
}) => {
  return (
    <div className="space-y-6" data-testid="step-3-card">
      {/* Options Card */}
      <div className="card">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--color-navy)' }}>
          Review and Pay
        </h2>
        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            {/* Turnaround Time */}
            <div className="space-y-2">
              <Label className="input-label">Turnaround Time *</Label>
              <Select
                value={form.turnaround}
                onValueChange={(value) => setForm({ ...form, turnaround: value })}
              >
                <SelectTrigger className="input-styled" data-testid="turnaround-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (7 working days)</SelectItem>
                  <SelectItem value="priority">Priority (5 working days) +25%</SelectItem>
                  <SelectItem value="express">Express (3 working days) +50%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Add-ons */}
            <div className="space-y-3">
              <Label className="input-label">Tender pack add-ons</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="addWorkmanshipSpec"
                      checked={form.addWorkmanshipSpec}
                      onCheckedChange={(checked) => setForm({ ...form, addWorkmanshipSpec: checked })}
                    />
                    <Label htmlFor="addWorkmanshipSpec" className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                      Workmanship Specification
                    </Label>
                  </div>
                  <span className="text-sm" style={{ color: 'var(--color-muted)' }}>+£79</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="addCoverLetter"
                      checked={form.addCoverLetter}
                      onCheckedChange={(checked) => setForm({ ...form, addCoverLetter: checked })}
                    />
                    <Label htmlFor="addCoverLetter" className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                      Professional Covering Letter
                    </Label>
                  </div>
                  <span className="text-sm" style={{ color: 'var(--color-muted)' }}>+£4.99</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="addTenderReturnForm"
                      checked={form.addTenderReturnForm}
                      onCheckedChange={(checked) => setForm({ ...form, addTenderReturnForm: checked })}
                    />
                    <Label htmlFor="addTenderReturnForm" className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                      Tender Return Form
                    </Label>
                  </div>
                  <span className="text-sm" style={{ color: 'var(--color-muted)' }}>+£4.99</span>
                </div>
              </div>
            </div>
          </div>

          {/* Workmanship Info */}
          {form.addWorkmanshipSpec && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
                Why the workmanship specification matters
              </p>
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                {quote.notes.workmanshipImportance}
              </p>
            </div>
          )}

          <button 
            onClick={onBack} 
            className="btn-secondary w-full flex items-center justify-center gap-2" 
            data-testid="step3-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Price Summary Card */}
      <div className="card">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--color-navy)' }}>
          Price Summary
        </h2>
        <div className="space-y-5">
          {/* Discount Code */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
            <Label className="input-label mb-2 block">Have a discount code?</Label>
            {discountApplied ? (
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#DCFCE7', border: '1px solid #86EFAC' }}>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" style={{ color: '#16A34A' }} />
                  <span className="font-semibold" style={{ color: '#15803D' }}>
                    {discountPercent}% discount applied!
                  </span>
                </div>
                <button onClick={onRemoveDiscount} className="text-sm font-medium" style={{ color: '#DC2626' }}>
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  className="input-styled flex-1"
                  data-testid="discount-code-input"
                  placeholder="Enter discount code"
                  value={form.discountCode}
                  onChange={(e) => {
                    setForm({ ...form, discountCode: e.target.value });
                    setDiscountError('');
                  }}
                />
                <button
                  onClick={onApplyDiscount}
                  className="btn-secondary"
                  data-testid="apply-discount-btn"
                >
                  Apply
                </button>
              </div>
            )}
            {discountError && (
              <p className="text-sm mt-2" style={{ color: '#DC2626' }}>{discountError}</p>
            )}
          </div>

          {/* Call Mode Notice */}
          {quote.mode === 'call' && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#DBEAFE', border: '1px solid #93C5FD' }}>
              <p className="text-sm" style={{ color: '#1E40AF' }}>
                This project requires a fact-finding call to provide an accurate quote.
              </p>
            </div>
          )}

          {/* Breakdown */}
          <div className="space-y-3">
            {quote.breakdown.map((item, index) => (
              <div key={`${item.label}-${index}`} className="flex justify-between items-start">
                <div>
                  <p style={{ color: 'var(--color-text)' }}>{item.label}</p>
                  {item.note && <p className="text-xs" style={{ color: 'var(--color-muted)' }}>{item.note}</p>}
                </div>
                <p className="font-semibold" style={{ color: 'var(--color-text)' }}>{gbp(item.amount)}</p>
              </div>
            ))}
            {discountApplied && (
              <div className="flex justify-between items-start" style={{ color: '#16A34A' }}>
                <p className="font-semibold">Discount ({discountPercent}% off)</p>
                <p className="font-semibold">-{gbp(quote.totalNow * (discountPercent / 100))}</p>
              </div>
            )}
          </div>

          {/* Total */}
          <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
            <div className="flex justify-between items-center">
              <p className="text-xl font-semibold" style={{ color: 'var(--color-navy)' }}>Total due now</p>
              <div className="text-right">
                {discountApplied && (
                  <p className="text-sm line-through" style={{ color: 'var(--color-muted)' }}>{gbp(quote.totalNow)}</p>
                )}
                <p className="text-2xl font-semibold" style={{ color: discountApplied ? '#16A34A' : 'var(--color-navy)' }}>
                  {gbp(finalAmount)}
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            Any quantities included are indicative and provided for guidance only. Contractors must measure, verify and price the works independently.
          </p>

          {/* T&C Checkbox */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'white', border: '2px solid var(--color-border)' }}>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="termsAccepted"
                data-testid="terms-checkbox"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked)}
                className="mt-1"
              />
              <Label htmlFor="termsAccepted" className="text-sm leading-relaxed cursor-pointer" style={{ color: 'var(--color-muted)' }}>
                I confirm that I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: 'var(--color-navy)' }}>
                  Terms and Conditions
                </a>
                , including the no-refund policy once work has commenced.
              </Label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA' }}>
              <p className="text-sm" style={{ color: '#B91C1C' }}>{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            data-testid="submit-payment-btn"
            onClick={onSubmit}
            disabled={busy || !quote.hasMinimumInfo || !termsAccepted}
            className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Processing...' : 
             discountApplied && finalAmount === 0 ? 'Submit (Free)' :
             quote.mode === 'call' ? 'Pay £79 and book call' : 'Pay now'}
          </button>
        </div>
      </div>

      {/* Upsell Services */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>
          <a href="https://www.macventure.co.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">
            MacVenture Consultancy Limited
          </a> Support Services
        </h2>
        <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
          These are not included in today's checkout. Tick any that you want a quote for.
        </p>
        <div className="space-y-3">
          {[
            { id: 'upsell_contractSupport', label: 'Contract support (contract data, schedules, and setup)' },
            { id: 'upsell_tenderAnalysis', label: 'Tender analysis and reporting with tender recommendation' },
            { id: 'upsell_tenderClarifications', label: 'Tender clarification management and addendum issue' },
            { id: 'upsell_valueEngineering', label: 'Value engineering and scope optimisation to meet budget' }
          ].map((item) => (
            <div key={item.id} className="flex items-center space-x-2">
              <Checkbox
                id={item.id}
                checked={form[item.id]}
                onCheckedChange={(checked) => setForm({ ...form, [item.id]: checked })}
              />
              <Label htmlFor={item.id} className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                {item.label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
